<template>
  <div>
    <v-skeleton-loader
      v-if="loading"
      class="m-auto"
      type="table"
      transition="scale-transition"
    />

    <TableRemote
      v-else
      :items="displayedWorkOrders"
      :title="$t('services.fieldServiceBilling')"
      :elementsPerPage="queryParams.perpage"
      :pagination="pagination"
      :headers="tableHeaders"
      hideDefaultFooter
      :isInfinitScroll="true"
      :sortableColumns="sortableColumns"
      :isSearchable="false"
      :centerColumns="['billable', 'invoiced']"
      :notSortableColumns="['billable', 'invoiced']"
      @sendQueryParams="fetchQuery"
    >
      <template #SearchWithFilters>
        <SearchWithFilters
          :filters="filters"
          :searchPlaceholder="$t('billing.filter.searchBy')"
          :datesRange="true"
          :filterName="filterName"
          :saveFilter="true"
          @onApplyFilters="handleApplyFilters"
          @onSearch="handleSearch"
        />
      </template>
      <template v-slot:[`item.billable`]="{item}">
        <VCheckbox
          v-model="item.billable"
          color="primary"
          class="m-0 p-0 d-flex justify-center"
          disabled
        />
      </template>

      <template v-slot:[`item.status`]="{item}">
        <v-icon
          v-if="(item.status === status.TODO)"
          color="primary"
          class="completionStatusIcon"
        >
          panorama_fish_eye
        </v-icon>
        <v-icon
          v-if="item.status === status.IN_PROGRESS"
          color="primary"
          class="completionStatusIcon"
        >
          timelapse
        </v-icon>
        <v-icon
          v-if="item.status === status.PENDING"
          color="primary"
          class="completionStatusIcon"
        >
          mdi-clock-outline
        </v-icon>
        <v-icon
          v-if="item.status === status.LATE"
          color="primary"
          class="completionStatusIcon"
        >
          mdi-alert-circle-outline
        </v-icon>
        <v-icon
          v-if="(item.status === status.COMPLETED)"
          color="primary"
          class="completionStatusIcon"
        >
          check_circle_outline
        </v-icon>
      </template>
      <template v-slot:[`item.invoiced`]="{item}">
        <VCheckbox
          v-model="item.invoiced"
          color="primary"
          class="m-0 p-0 d-flex justify-center"
          @input="()=>billingStatusUpdate(item.id,item.invoiced)"
        />
      </template>
      <template #withInfiniteScroll>
        <div v-intersect="endIntersect" />
      </template>
    </TableRemote>
  </div>
</template>

<script>
import TableRemote from '@/components/shared/content-views/VTableRemote';
import workOrderStatusChoices from '@/constants/workOrderStatusChoices';
import VCheckbox from '@/components/shared/widgets/forms/VuetifyInputs/VCheckbox.vue';
import SearchWithFilters from '@/components/shared/SearchWithFilters.vue';
import moment from 'moment-timezone';
import { mapGetters } from 'vuex';

export default {
    components: {
        TableRemote,
        VCheckbox,
        SearchWithFilters,
    },
    data() {
        return {
            billing: [],
            loading: false,
            workOrders: [],
            pagination: {},
            queryParams: {
                page: 1,
                perpage: 100,
                search: this.search,
                completionStatus: workOrderStatusChoices.COMPLETED,
                dateFrom: null,
                dateTo: null,
                billable: true,
                invoiced: false,
                workOrderTypes: '',
            },
            filterName: 'serviceBillingField',
            filters: [
                {
                    key: 'type',
                    label: 'billing.filter.wOType',
                    type: 'select',
                    value: [],
                    multiple: true,
                    items: [],
                    datacy: 'billingFilterWoType',
                },
                {
                    key: 'status',
                    label: 'billing.filter.status',
                    type: 'select',
                    value: [workOrderStatusChoices.COMPLETED],
                    multiple: true,
                    items: [],
                    datacy: 'billingFilterStatus',
                },
                {
                    key: 'date',
                    label: 'billing.filter.date',
                    type: 'date',
                    value: [],
                    dateKeys: ['startDate', 'endDate'],
                    isRange: true,
                    datacy: 'billingFilterDate',
                },
                {
                    key: 'billable',
                    label: 'billing.filter.billing',
                    type: 'select',
                    value: ['billable'],
                    multiple: true,
                    items: [
                        { text: 'billing.filter.billable', value: 'billable' },
                        { text: 'billing.filter.invoiced', value: 'invoiced' },
                    ],
                    datacy: 'billingFilterBillable',
                },
            ],
        };
    },

    computed: {
        ...mapGetters([
            'lang',
        ]),
        status() {
            return workOrderStatusChoices;
        },
        completionStatus() {
            return Object.values(this.status).map(status => ({
                value: status,
                text: this.$t(`forms.labels.${status}`),
                isAlreadyTranslated: true,
            }));
        },
        headers() {
            const mobileHeaders = [
                'clientNumber',
                'siteNumber',
                'billable',
                'status',
                'invoiced',
            ];

            const headers = [
                'clientNumber',
                'siteNumber',
                'wONumber',
                'wOType',
                'wOdate',
                'assignedTo',
                'onSiteDurationHours',
                'poNumber',
                'soNumber',
                'billable',
                'status',
                'invoiced',
            ];
            return this.$vuetify.breakpoint.mdAndDown ? mobileHeaders : headers;
        },
        tableHeaders() {
            return this.headers.map(header => ({
                text: this.$t(`billing.labels.${header}`),
                value: header,
            }));
        },
        sortableColumns() {
            return this.headers.map(column => this.$options.filters.lowercase(column));
        },
        displayedWorkOrders() {
            return this.workOrders.map(workOrder => ({
                ...workOrder,
                clientNumber: workOrder.client.accountNumber,
                siteNumber: workOrder.site.deliveryAccountNumber,
                assignedTo: this.getAssignedTo(workOrder.assignedTo),
                onSiteDurationHours: workOrder.onSiteDurationHours,
                wONumber: workOrder.contractNumber,
                wOType: workOrder.workOrderType.name,
                wOdate: workOrder.startDate ? this.$options.filters.formatDate(workOrder.startDate) : null,
                status: workOrder.completionStatus,
                poNumber: workOrder.poNumber || null,
                soNumber: workOrder.soNumber || null,

            }));
        },
    },
    created() {
        this.getTypes();
    },
    methods: {
        async getTypes() {
            this.loading = true;
            const { data } = await this.$rq.getWorkOrderTypes();
            const woType = data.map(({ id }) => id);
            this.queryParams.workOrderTypes = woType;
            this.filters[0].items = data.map(type => ({
                value: type.id,
                text: type.name[this.lang],
                isAlreadyTranslated: true,
            }));
            this.filters[0].value = woType;
            this.filters[1].items = this.completionStatus;
            await this.getBilling();
            this.loading = false;
        },
        async getBilling() {
            const cleanQueryParams = this.$utils.removeEmptyNullOrUndefined(this.queryParams, true);
            const { data } = await this.$rq.getBilling(cleanQueryParams);
            this.workOrders = [...this.workOrders, ...data.workOrders.docs];
            this.pagination = data.pagination;
        },
        async billingStatusUpdate(id, invoicedStatus) {
            await this.$rq.billingStatusUpdate(id, { invoiced: invoicedStatus });
            await this.fetchQuery();
        },
        getAssignedTo(user) {
            if (!user) {
                return null;
            }
            const [assignedTo] = user;
            const { firstName, lastName } = assignedTo.user.profile;
            return `${firstName} ${lastName}`;
        },
        async fetchQuery(queryParams = {}) {
            this.queryParams = {
                ...this.queryParams,
                ...queryParams,
                order: this.preparOrderQueryParams(queryParams.order),
                page: 1,
            };
            this.pagination = {};
            this.workOrders = [];
            await this.getBilling();
        },
        preparOrderQueryParams(value) {
            if (typeof value !== 'string') {
                return null;
            }

            const [name, order] = value.split('_');
            let newValue;

            switch (name) {
                case 'onsitedurationhours':
                    newValue = 'workhours';
                    break;
                case 'wonumber':
                    newValue = 'contractnumber';
                    break;
                case 'wotype':
                    newValue = 'workordertype';
                    break;
                case 'wodate':
                    newValue = 'createdAt';
                    break;
                case 'status':
                    newValue = 'completionstatus';
                    break;
                default:
                    return value;
            }
            return `${newValue}_${order}`;
        },
        async endIntersect(_, __, isIntersecting) {
            const condition = this.pagination.currentPage < this.pagination.totalPageCount;
            if (isIntersecting && condition) {
                this.queryParams.page = this.pagination.currentPage + 1;
                await this.getBilling();
                const ref = this.$refs?.tableRemoteRef?.$refs?.tableRemote;
                if (ref?.onScroll) {
                    ref.onScroll();
                }
            }
        },
        handleSearch(value) {
            this.queryParams.search = value;
            this.fetchQuery();
        },
        handleApplyFilters(queryParams) {
            this.queryParams = {
                page: 1,
                perpage: 100,
                search: queryParams.reset ? '' : this.queryParams.search,
                completionStatus: queryParams.status.join(','),
                dateFrom: queryParams.startDate ? moment(queryParams.startDate).tz('utc').startOf('day').toISOString() : null,
                dateTo: queryParams.endDate ? moment(queryParams.endDate).tz('utc').endOf('day').toISOString() : null,
                billable: queryParams.billable.includes('billable'),
                invoiced: queryParams.billable.includes('invoiced'),
                workOrderTypes: queryParams.type.join(','),
            };
            this.fetchQuery();
        },
    },
};
</script>
